import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "components/AppIcon";

const MODE_CONFIG = {
  "Bank Deposit":           { icon: "Building2",      color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"       },
  "Cash":                   { icon: "Banknote",       color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"    },
  "Cheque":                 { icon: "FileCheck",      color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300" },
  "Credit Card":            { icon: "CreditCard",     color: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300" },
  "Debit Card":             { icon: "CreditCard",     color: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300" },
  "Demand Draft":           { icon: "FileText",       color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300" },
  "Online Payment Gateway": { icon: "Globe",          color: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300"        },
  "Online Transfer":        { icon: "ArrowRightLeft", color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300"        },
  "PayPal":                 { icon: "Wallet",         color: "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300"            },
  "Wire Transfer":          { icon: "Zap",            color: "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300"        },
};

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
];

const ActionBtn = ({ icon, label, color, onClick }) => (
  <div className="relative group">
    <button onClick={onClick} className={`p-1.5 rounded-lg transition-colors ${color}`}>
      <Icon name={icon} size={14} />
    </button>
    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 border border-border">
      {label}
    </div>
  </div>
);

const PurchasePaymentTable = ({ payments, onEdit, onDelete }) => {
  if (!payments.length) return null;

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              {["Date", "Expense #", "Vendor", "Mode", "Paid From", "Amount (SR)", "Reference", "Actions"].map((h) => (
                <th key={h} className="px-5 py-4 text-left text-sm font-medium text-foreground whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {payments.map((payment, index) => (
              <PaymentRow key={payment.id} payment={payment} index={index} onEdit={onEdit} onDelete={onDelete} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const PaymentRow = ({ payment, index, onEdit, onDelete }) => {
  const [showDelete, setShowDelete] = useState(false);

  const modeConfig  = MODE_CONFIG[payment.paymentMode] || { icon: "CircleDollarSign", color: "bg-gray-100 text-gray-600" };
  const initials    = (payment.vendorName || "?").split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: index * 0.04 }}
        className="hover:bg-muted/50 transition-colors"
      >
        {/* Date */}
        <td className="px-5 py-4 text-sm text-foreground whitespace-nowrap">
          {payment.date
            ? new Date(payment.date).toLocaleDateString("en-SA", { year: "numeric", month: "short", day: "numeric" })
            : "—"}
        </td>

        {/* Expense # */}
        <td className="px-5 py-4">
          {payment.expenseNumber
            ? <span className="font-mono text-sm text-primary font-semibold">{payment.expenseNumber}</span>
            : <span className="text-sm text-muted-foreground">—</span>}
        </td>

        {/* Vendor */}
        <td className="px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${avatarColor}`}>
              {initials}
            </div>
            <span className="text-sm font-medium text-foreground">{payment.vendorName || "—"}</span>
          </div>
        </td>

        {/* Mode */}
        <td className="px-5 py-4">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${modeConfig.color}`}>
            <Icon name={modeConfig.icon} size={11} />
            {payment.paymentMode}
          </span>
        </td>

        {/* Paid From */}
        <td className="px-5 py-4">
          {payment.paidFrom
            ? <span className="text-sm text-muted-foreground">{payment.paidFrom}</span>
            : <span className="text-sm text-muted-foreground">—</span>}
        </td>

        {/* Amount */}
        <td className="px-5 py-4">
          <span className="font-semibold text-red-500">
            − {parseFloat(payment.amount || 0).toLocaleString("en-SA", { minimumFractionDigits: 2 })}
          </span>
        </td>

        {/* Reference */}
        <td className="px-5 py-4">
          {payment.reference
            ? <span className="font-mono text-sm bg-muted px-2 py-0.5 rounded text-foreground">{payment.reference}</span>
            : <span className="text-sm text-muted-foreground">—</span>}
        </td>

        {/* Actions */}
        <td className="px-5 py-4">
          <div className="flex items-center gap-1">
            <ActionBtn icon="Edit3"  label="Edit Payment"   color="text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20"  onClick={() => onEdit(payment)} />
            <ActionBtn icon="Trash2" label="Delete Payment" color="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"    onClick={() => setShowDelete(true)} />
          </div>
        </td>
      </motion.tr>

      <AnimatePresence>
        {showDelete && (
          <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <td colSpan={8} className="px-5 py-3 bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-800">
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                  Delete payment of{" "}
                  <strong>SR {parseFloat(payment.amount || 0).toLocaleString("en-SA", { minimumFractionDigits: 2 })}</strong>{" "}
                  to <strong>{payment.vendorName}</strong>? This cannot be undone.
                </span>
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  <button onClick={() => setShowDelete(false)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors">
                    Cancel
                  </button>
                  <button onClick={() => { onDelete(payment.id); setShowDelete(false); }}
                    className="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors">
                    Delete
                  </button>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
};

export default PurchasePaymentTable;